import { z } from 'zod'

import type { WorkspaceTrialStatus } from './trial'
import { FeatureValueType } from './subscription-plans'

// Subscription plan status
export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused',
}

// Feature value types (imported from subscription-plans to avoid circular dependency)

// Feature categories
export enum FeatureCategory {
  ANALYTICS = 'analytics',
  COLLABORATION = 'collaboration',
  SECURITY = 'security',
  INTEGRATION = 'integration',
  STORAGE = 'storage',
  SUPPORT = 'support',
  CUSTOMIZATION = 'customization',
  API = 'api',
}

// Billing intervals
export enum BillingInterval {
  MONTH = 'month',
  YEAR = 'year',
}

// Subscription Plan Schema
export const SubscriptionPlanSchema = z.object({
  id: z.string().uuid(),
  key: z.string(), // e.g., 'free', 'team', 'business', 'enterprise'
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullable().optional(),
  stripeProductId: z.string().nullable().optional(),
  stripePriceId: z.string().nullable().optional(), // Monthly price ID
  stripeAnnualPriceId: z.string().nullable().optional(), // Annual price ID
  price: z.number(), // Monthly price (total or per-user)
  annualPrice: z.number().nullable().optional(), // Annual price (with discount)
  pricePerUser: z.number().nullable().optional(), // Per-user pricing
  minSeats: z.number().int().min(1).default(1),
  currency: z.string().default('USD'),
  interval: z.nativeEnum(BillingInterval),
  intervalCount: z.number().default(1),
  trialPeriodDays: z.number().nullable().optional(),
  isActive: z.boolean().default(true),
  contactSales: z.boolean().default(false), // For enterprise plans
  isComingSoon: z.boolean().default(false), // For plans that are coming soon
  metadata: z.record(z.any()).default({}),
  features: z.record(z.any()).default({}),
  limits: z.record(z.number()).default({}),
  sortOrder: z.number().default(0),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>

// Subscription Feature Schema
export const SubscriptionFeatureSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  category: z.nativeEnum(FeatureCategory),
  valueType: z.nativeEnum(FeatureValueType),
  defaultValue: z.any(),
  metadata: z.record(z.any()).default({}),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type SubscriptionFeature = z.infer<typeof SubscriptionFeatureSchema>

// Organization Subscription Schema
export const OrganizationSubscriptionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  planId: z.string().uuid(),
  stripeCustomerId: z.string(),
  stripeSubscriptionId: z.string(),
  status: z.nativeEnum(SubscriptionStatus),
  currentPeriodStart: z.date(),
  currentPeriodEnd: z.date(),
  cancelAt: z.date().nullable().optional(),
  canceledAt: z.date().nullable().optional(),
  trialStart: z.date().nullable().optional(),
  trialEnd: z.date().nullable().optional(),
  metadata: z.record(z.any()).default({}),
  // Internal trial payment setup state
  requiresPaymentSetup: z.boolean().optional(),
  paymentSetupCanStart: z.boolean().optional(),
  paymentSetupCanPreserveTrial: z.boolean().optional(),
  paymentSetupAvailableAt: z.date().nullable().optional(),
  paymentSetupDueAt: z.date().nullable().optional(),
  // Seat-based billing fields
  seats: z.number().default(1),
  minSeats: z.number().default(1),
  maxSeats: z.number().nullable().optional(),
  seatPrice: z.number().nullable().optional(),
  annualSeatPrice: z.number().nullable().optional(),
  allowSeatReduction: z.boolean().default(true),
  billingAnchorDate: z.date().nullable().optional(),
  // AI usage credits
  aiCredits: z.number().default(0),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  plan: SubscriptionPlanSchema.optional(),
})

export type OrganizationSubscription = z.infer<
  typeof OrganizationSubscriptionSchema
> & {
  /**
   * No-card trial state (docs/PLAN-no-card-trials.md). Present only while the
   * organization is on a cardless trial of a paid plan and the NO_CARD_TRIALS
   * rollout is enabled; null for activated/paid and personal workspaces.
   */
  trial?: WorkspaceTrialStatus | null
}

// Create Subscription DTO
export const CreateSubscriptionDtoSchema = z.object({
  planId: z.string().uuid(),
  billingInterval: z.nativeEnum(BillingInterval).optional(), // monthly or annual
  paymentMethodId: z.string().optional(),
  promoCode: z.string().optional(), // Promo code to apply
  trialDays: z.number().optional(),
  seats: z.number().min(1).optional(), // Initial seat count
  metadata: z.record(z.any()).optional(),
})

export type CreateSubscriptionDto = z.infer<typeof CreateSubscriptionDtoSchema>

// Update Subscription DTO
export const UpdateSubscriptionDtoSchema = z.object({
  planId: z.string().uuid().optional(),
  cancelAtPeriodEnd: z.boolean().optional(),
  metadata: z.record(z.any()).optional(),
})

export type UpdateSubscriptionDto = z.infer<typeof UpdateSubscriptionDtoSchema>

// Subscription Usage Schema
export const SubscriptionUsageSchema = z.object({
  id: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  featureKey: z.string(),
  quantity: z.number(),
  timestamp: z.date(),
  metadata: z.record(z.any()).default({}),
  reportedToStripe: z.boolean().default(false),
  createdAt: z.date().optional(),
})

export type SubscriptionUsage = z.infer<typeof SubscriptionUsageSchema>

// Report Usage DTO
export const ReportUsageDtoSchema = z.object({
  featureKey: z.string(),
  quantity: z.number(),
  timestamp: z.date().optional(),
  metadata: z.record(z.any()).optional(),
})

export type ReportUsageDto = z.infer<typeof ReportUsageDtoSchema>

// Feature Override Schema
export const FeatureOverrideSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  featureId: z.string().uuid(),
  value: z.any(),
  reason: z.string().nullable().optional(),
  expiresAt: z.date().nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

export type FeatureOverride = z.infer<typeof FeatureOverrideSchema>

// Create Feature Override DTO
export const CreateFeatureOverrideDtoSchema = z.object({
  featureKey: z.string(),
  value: z.any(),
  reason: z.string().optional(),
  expiresAt: z.date().optional(),
})

export type CreateFeatureOverrideDto = z.infer<
  typeof CreateFeatureOverrideDtoSchema
>

// Billing Portal Session DTO
export const CreateBillingPortalSessionDtoSchema = z.object({
  returnUrl: z.string().url(),
})

export type CreateBillingPortalSessionDto = z.infer<
  typeof CreateBillingPortalSessionDtoSchema
>

export const CreateTrialPaymentSetupSessionDtoSchema = z.object({
  returnUrl: z.string().url(),
})

export type CreateTrialPaymentSetupSessionDto = z.infer<
  typeof CreateTrialPaymentSetupSessionDtoSchema
>

// Seat Management DTOs
export const UpdateSeatsDto = z.object({
  seats: z.number().min(1),
  immediate: z.boolean().optional(), // For monthly plans to apply immediately vs next cycle
})

export type UpdateSeatsDto = z.infer<typeof UpdateSeatsDto>

export const SeatAssignmentDto = z.object({
  userId: z.string().uuid(),
  active: z.boolean(),
})

export type SeatAssignmentDto = z.infer<typeof SeatAssignmentDto>

export const SeatUsageDto = z.object({
  totalSeats: z.number(),
  usedSeats: z.number(),
  availableSeats: z.number(),
  assignments: z.array(
    z.object({
      userId: z.string().uuid(),
      userEmail: z.string().email(),
      userName: z.string(),
      assignedAt: z.date(),
      assignedBy: z.string(),
    }),
  ),
  pendingInvites: z.number().optional(), // Number of pending invites counting as reserved seats
})

export type SeatUsageDto = z.infer<typeof SeatUsageDto>

// Seat Change History
export const SeatChangeHistoryDto = z.object({
  id: z.string().uuid(),
  previousSeats: z.number(),
  newSeats: z.number(),
  changeType: z.enum(['increase', 'decrease']),
  reason: z.string().nullable().optional(),
  prorationAmount: z.number().nullable().optional(),
  changedBy: z.string(),
  changedByName: z.string(),
  createdAt: z.date(),
})

export type SeatChangeHistoryDto = z.infer<typeof SeatChangeHistoryDto>

// Checkout Session DTO with seats
export const CreateCheckoutSessionDto = z.object({
  planId: z.string().uuid(),
  billingInterval: z.nativeEnum(BillingInterval).optional(), // monthly or annual
  promoCode: z.string().optional(), // Promo code to apply
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
  trialDays: z.number().optional(),
  seats: z.number().min(1).optional(), // Initial seat count
  metadata: z.record(z.any()).optional(),
})

export type CreateCheckoutSessionDto = z.infer<typeof CreateCheckoutSessionDto>

// Re-export subscription plans
export { DEFAULT_SUBSCRIPTION_PLANS } from './subscription-plans'

// Default subscription plans configuration
export const DEFAULT_PLANS = {
  BASIC: {
    name: 'basic',
    displayName: 'Basic',
    description: 'Perfect for small teams getting started',
    features: {
      maxUsers: 5,
      maxProjects: 10,
      storageGB: 10,
      advancedAnalytics: false,
      prioritySupport: false,
    },
  },
  PRO: {
    name: 'pro',
    displayName: 'Professional',
    description: 'For growing teams that need more power',
    features: {
      maxUsers: 25,
      maxProjects: 50,
      storageGB: 100,
      advancedAnalytics: true,
      prioritySupport: false,
    },
  },
  ENTERPRISE: {
    name: 'enterprise',
    displayName: 'Enterprise',
    description: 'For large organizations with advanced needs',
    features: {
      maxUsers: -1, // unlimited
      maxProjects: -1, // unlimited
      storageGB: 1000,
      advancedAnalytics: true,
      prioritySupport: true,
    },
  },
}

// Import feature keys from subscription-plans to avoid duplication
export { FEATURE_KEYS, type FeatureKey } from './subscription-plans'

// Plan validation types
export interface UsageValidationResult {
  canDowngrade: boolean
  violations: UsageViolation[]
  currentUsage: OrganizationUsage
  targetLimits: PlanLimits
}

export interface UsageViolation {
  type: 'users' | 'projects' | 'storage' | 'features'
  current: number | boolean
  limit: number | boolean
  message: string
  suggestion?: string
}

export interface OrganizationUsage {
  userCount: number
  projectCount: number
  storageUsageGB: number
  activeFeatures: string[]
}

export interface PlanLimits {
  maxUsers: number // -1 = unlimited
  maxProjects: number // -1 = unlimited
  storageGB: number // -1 = unlimited
  allowedFeatures: string[]
}
